import React from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { Vehicle } from '../../../data/models/vehicle.model';
import {
  GenericDocument,
  VehicleDocument,
} from '../../../data/models/document.model';
import * as vehicleSlice from '../../../data/vehicle.slice';
import * as referenceSlice from '../../../data/reference.slice';
import { GenericDocumentType } from '../../../data/models/document-type.model';
import documentUtils from '../../../services/documentUtils';
import DocumentList from '../DocumentList';

interface VehicleDocumentsProps {
  vehicle: Vehicle | null;
  uploadingDocument: boolean;
  readOnly: boolean;
  allowView: boolean;
}

export default function VehicleDocuments({
  vehicle,
  uploadingDocument,
  readOnly,
  allowView,
}: VehicleDocumentsProps) {
  const dispatch = useAppDispatch();

  const vehicleDocuments = useAppSelector(vehicleSlice.selectVehicleDocuments);
  const vehicleDocumentTypes = useAppSelector(
    referenceSlice.selectVehicleDocumentTypes,
  );

  const [documents, setDocuments] = React.useState<GenericDocument[]>([]);
  const [documentTypes, setDocumentTypes] = React.useState<
    GenericDocumentType[]
  >([]);

  React.useEffect(() => {
    if (vehicle && vehicle.vehicleID) {
      dispatch(vehicleSlice.fetchVehicleDocumentsAsync(vehicle.vehicleID));
    }
  }, [vehicle, dispatch]);

  React.useEffect(() => {
    if (vehicle && vehicleDocuments && vehicleDocumentTypes) {
      const docTypes =
        documentUtils.vehicleTypesToGenericTypes(vehicleDocumentTypes);
      const docs = documentUtils.vehicleToGeneric(
        vehicleDocuments,
        vehicleDocumentTypes,
      );
      setDocuments(docs);
      setDocumentTypes(docTypes);
    }
  }, [vehicle, vehicleDocuments, vehicleDocumentTypes]);

  const saveVehicleDocument = (
    file: any,
    typeId: number,
    signedOn: string | null,
    effectiveStart: string | null,
    effectiveEnd: string | null,
  ) => {
    if (vehicle && vehicle.vehicleID) {
      const doc: VehicleDocument = {
        vehicleDocumentTypeID: typeId,
        vehicleDocumentState: 'active',
        signedOnDate: signedOn || null,
        effectiveStartDate: effectiveStart || null,
        effectiveEndDate: effectiveEnd || null,
      };
      dispatch(
        vehicleSlice.uploadVehicleDocumentAsync(vehicle.vehicleID, file, doc),
      );
    }
  };

  const updateVehicleDocument = (
    documentId: number,
    typeId: number,
    signedOn: string | null,
    effectiveStart: string | null,
    effectiveEnd: string | null,
  ) => {
    if (vehicle && vehicle.vehicleID) {
      const doc: VehicleDocument = {
        vehicleDocumentID: documentId,
        vehicleDocumentTypeID: typeId,
        vehicleDocumentState: 'active',
        signedOnDate: signedOn || null,
        effectiveStartDate: effectiveStart || null,
        effectiveEndDate: effectiveEnd || null,
      };
      dispatch(vehicleSlice.updateVehicleDocumentAsync(vehicle.vehicleID, doc));
    }
  };

  const deleteVehicleDocument = (id: number) => {
    if (vehicle && vehicle.vehicleID) {
      dispatch(vehicleSlice.deleteVehicleDocumentAsync(vehicle.vehicleID, id));
    }
  };

  return (
    <DocumentList
      providerID={vehicle!.transportationProviderID}
      documents={documents}
      documentTypes={documentTypes}
      uploadingDocument={uploadingDocument}
      readOnly={readOnly}
      allowView={allowView}
      save={saveVehicleDocument}
      update={updateVehicleDocument}
      doDelete={deleteVehicleDocument}
    />
  );
}
